import { useNavigate } from 'react-router-dom';
import type { WesteerModule } from '../../auth/types';
import { WORKSPACE_REGISTRY, isWorkspaceEnabled } from '../../workspaces';
import type { WorkspaceDefinition } from '../../workspaces';

// ─── Status helper ────────────────────────────────────────────────────────────

export function resolveDockStatus(
  ws: WorkspaceDefinition,
  hasProject: boolean,
  projectModules: WesteerModule[] | undefined
): 'active' | 'disabled' {
  if (ws.requiresProject && !hasProject) return 'disabled';
  if (ws.status === 'future') return 'disabled';
  if (!isWorkspaceEnabled(ws, projectModules)) return 'disabled';
  return 'active';
}

// ─── Dock button ──────────────────────────────────────────────────────────────

interface DockButtonProps {
  label: string;
  isTerritory?: boolean;
  isDisabled?: boolean;
  onClick: () => void;
}

function DockButton({ label, isTerritory, isDisabled, onClick }: DockButtonProps): JSX.Element {
  const cls = [
    'ts-dock-btn',
    isTerritory ? 'ts-dock-btn--territory' : '',
    isDisabled ? 'ts-dock-btn--disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button className={cls} onClick={onClick} disabled={isDisabled} type="button">
      <span className="ts-dock-btn-label">{label}</span>
    </button>
  );
}

// ─── Dock ─────────────────────────────────────────────────────────────────────

export interface TerritorialDockProps {
  hasProjects: boolean;
  projectModules: WesteerModule[] | undefined;
  isSuperAdmin: boolean;
}

export function TerritorialDock({
  hasProjects,
  projectModules,
  isSuperAdmin,
}: TerritorialDockProps): JSX.Element {
  const navigate = useNavigate();
  const territoryWs = WORKSPACE_REGISTRY.find(ws => ws.id === 'territory');
  const otherWorkspaces = WORKSPACE_REGISTRY.filter(ws => ws.id !== 'territory');

  const goTo = (ws: WorkspaceDefinition, status: 'active' | 'disabled'): void => {
    if (status === 'active') navigate(ws.route);
  };

  const territoryStatus = territoryWs
    ? resolveDockStatus(territoryWs, hasProjects, projectModules)
    : 'disabled';

  return (
    <div className="ts-dock">
      <div className="ts-dock-inner">
        {territoryWs && (
          <DockButton
            label={territoryWs.shortTitle}
            isTerritory
            isDisabled={territoryStatus === 'disabled'}
            onClick={() => {
              // Territory Focus — activación in-place, pendiente Bloque 2.
            }}
          />
        )}
        {otherWorkspaces.map(ws => {
          const status = resolveDockStatus(ws, hasProjects, projectModules);
          return (
            <DockButton
              key={ws.id}
              label={ws.shortTitle}
              isDisabled={status === 'disabled'}
              onClick={() => goTo(ws, status)}
            />
          );
        })}
        {isSuperAdmin && <DockButton label="Admin" onClick={() => navigate('/admin/dashboard')} />}
      </div>
    </div>
  );
}
